<template>
  <div id="app-inner">
    <div class="router-view__wrapper">
      <template v-if="redirectToStandalonePage">
        <div class="reroute__container">
          <div class="logo_wrapper">
            <DeviceMobileIcon class="text-grey device-icon" size="25" />
          </div>
          <a class="text2" v-bind:href="dynamicLink" target="_PARENT">Check out our mobile-optimized site here</a>
          <div class="text1">We've noticed that you're on a mobile device and/or smaller screen size.</div>
        </div>
      </template>
      <template v-else>
        <router-view />
      </template>
    </div>
  </div>
</template>
<script>
import { defineComponent, onMounted, onBeforeUnmount, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { emitter } from '@/common/utils/emitter';
import { useLoading } from 'vue-loading-overlay'
import { DeviceMobileIcon } from '@heroicons/vue/solid';
import useBreakpoints from '@/common/ui/breakpoints';
import { MetricHandler } from '@/common/classes/metrics'

import { usageReuseFilter } from '@/common/utils/translator.utils';

const guessBrowser = (userAgent) => {
  if (userAgent.indexOf("Firefox") > -1) {
    return 'Mozilla-Firefox'
  } else if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Edg") === -1) {
    return 'Google-Chrome'
  } else if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) {
    return 'Safari'
  } else if (userAgent.indexOf("Edg") > -1) {
    return 'Edge'
  } else {
    return 'Unknown'
  } 
}


export default defineComponent({
  components: {
    DeviceMobileIcon
  },
  setup() {
    const { type } = useBreakpoints();
    const store = useStore();
    const router = useRouter();
    const $loading = useLoading();
    let loader = ref(null);
    let dynamicLink = ref(null);
    let isInIframe = ref(false);
    const isMobile = ref(false);
    let redirectToStandalonePage = ref(false)

    const isiFrameParent = window.self !== window.top;
    isInIframe.value = isiFrameParent
    isMobile.value = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    redirectToStandalonePage.value = isInIframe.value && isMobile.value;

    const metricHandler = new MetricHandler();
    store.dispatch('SETUP_METRIC_HANDLER', metricHandler)

    store.state.metricHandler.handleEvent({
      type: 'session:start',
      payload: {
        deviceType: guessBrowser(navigator.userAgent),
        timestamp: Date.now(),
        isMobile: isMobile.value
      }
    })

    const handleBeforeUnload = () => {};

    onMounted(() => {
      window.addEventListener('beforeunload', handleBeforeUnload);
    });

    onMounted(() => {
      window.addEventListener('beforeunload', handleBeforeUnload);

      if (process.env.VUE_APP_DEPLOY_TYPE === 'reuse'){
        store.commit('APPEND_FILTER', usageReuseFilter);
      }


      dynamicLink.value = window.location.href;

      emitter.on('switched:state', (s) => {
        if (!redirectToStandalonePage.value) {
          console.log('switching state')
          store.commit('SET_STATE', s);
          store.dispatch('QUERY_SITES_BY_STATE', s);

          router.replace({ name: 'state-view', params: { id: store.state.selectedState } });
        }
      });

      emitter.on('start:loader', () => {
        loader = $loading.show({ color: "#026fcf" });
      });

      emitter.on('cancel:loader', () => {
        if (loader && loader?.hide) loader.hide();
      });


    });
    onBeforeUnmount(() => {
      emitter.off('switched:state');
      emitter.off('start:loader');
      emitter.off('cancel:loader');
      window.removeEventListener('beforeunload', handleBeforeUnload);
    });

    const stateValue = window.history.state.current.split('/').pop();

    if (!redirectToStandalonePage.value) { // only boot app if not showing optimized mobile reroute view
      if (stateValue === 'national') {
        store.state.metricHandler.handleEvent({
          type: 'load:national-view',
          payload: {
            timestamp:  Date.now() 
          }
        })
        router.push({ name: 'national-view' });
        store.commit('BOOT_APP');
      } else if (stateValue) {

        store.state.metricHandler.handleEvent({
          type: 'load:state-view',
          payload: {
            timestamp:  Date.now(),
            state: stateValue 
          }
        })

        store.commit('SET_STATE', stateValue);
        store.dispatch('QUERY_SITES_BY_STATE', stateValue);
        router.push({ name: 'state-view', params: { id: stateValue } });
        store.commit('BOOT_APP');
      }
    }

    return {
      redirectToStandalonePage,
      dynamicLink,
      isInIframe
    }

  },
});
</script>
<style>
a.textRedirect {
  text-decoration: none;
  /* Remove the default underline */
  color: #0078d4;
  /* Set the link color */
  font-weight: bold;
  /* Make the text bold */
  padding: 5px 10px;
  /* Add some padding for clickable area */
  border: 2px solid #0078d4;
  /* Add a border for visual distinction */
  border-radius: 5px;
  /* Add rounded corners */
  background-color: #f2f2f2;
  /* Add a background color */
  transition: background-color 0.3s, color 0.3s;
  /* Add a smooth transition */
}

/* Change link styles on hover for user feedback */
a:hover {
  background-color: #0078d4;
  /* Change background color on hover */
  color: #fff;
  /* Change text color on hover */
}

.text1 {
  font-family: 'Roboto', sans-serif !important;
  font-size: 1.5rem;
  font-style: italic;
  font-weight: 300;
}

.logo_wrapper {
  display: flex;
  justify-content: center;
}

.device-icon {
  width: 10rem;
  height: 10rem;
}

.text2 {
  font-family: 'Roboto', sans-serif !important;
  font-size: 2rem;
  color: blue;
  font-weight: bold;
  text-decoration: underline;
  margin-top: 2rem;
  margin-bottom: 2rem;
}

.reroute__container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 5rem;
}

html {
  font-size: 62.5%;
  font-family: 'Roboto', sans-serif !important;
}

html body {
  height: 100vh;
  width: 100
}

html,
body,
#app,
#appinner {
  height: 100%;
  margin: 0px;
}

#appinner {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

#app-inner {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

.router-view__wrapper {
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
  justify-content: center;
}

@media only screen and (max-width: 550px) {
  .router-view__wrapper {
    max-width: 400px;
  }
}


/* WebKit (Chrome, Edge, Safari) */
*::-webkit-scrollbar {
  width: 10px;
}

*::-webkit-scrollbar-track {
  background-color: #f1f1f1;
}

*::-webkit-scrollbar-thumb {
  background-color: #59bfff;
  ;
  border-radius: 5px;
}

*::-webkit-scrollbar-thumb:hover {
  background-color: #555;
}
</style>